import { useMemo } from "react"

import type {
  ChartVisualisation,
  WidgetPayload,
  WidgetType
} from "../../../components/widgets/types.ts"
import { indexCatalog } from "../../DashboardPage/components/Portfolio/widgetsResponseAdapter.ts"
import type { CatalogWidgetSpec } from "../../DashboardPage/redux/widgets.types.ts"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { indexCatalogBySpecKey } from "../helpers/reportCatalog.ts"
import {
  addWidget,
  removeWidget,
  setWidgetCompare,
  setWidgetFilters,
  setWidgetSize,
  setWidgetTitle,
  setWidgetVisualisation
} from "../helpers/reportDefinition.ts"
import { adaptReportWidget } from "../helpers/reportWidgetAdapter.ts"
import {
  reportCanvasApplyDefinition,
  reportCanvasSetConfigWidgetId
} from "../redux/reportCanvas.slice.ts"
import type {
  ReportCompareOverride,
  ReportWidgetEntry,
  ReportWidgetFilters,
  WidgetSizePreset
} from "../types/canvas.types.ts"

const specWidgetType = (type: string | undefined): WidgetType | undefined =>
  type as WidgetType | undefined

// Which visualisation options a chart widget offers depends on the shape the
// backend resolved: time series can switch line/bar/area, a project breakdown is
// bar-only, and categorical (donut) charts and the lead engagement table expose
// no switch.
const visualisationOptionsFor = (
  type: string | undefined,
  payload: WidgetPayload | null
): ChartVisualisation[] | null => {
  if (type !== "chart") return null
  const chart = payload?.chart
  if (chart?.engagement) return null
  if (chart?.trend || chart?.cashFlow) return ["line", "bar", "area"]
  if (chart?.breakdown) return ["bar"]
  return null
}

export interface ReportBuildActions {
  selectedWidget: ReportWidgetEntry | null
  selectedSpec: CatalogWidgetSpec | undefined
  visualisationOptions: ChartVisualisation[] | null
  addWidget: (spec: CatalogWidgetSpec) => void
  selectWidget: (id: string | null) => void
  removeWidget: (id: string) => void
  changeTitle: (id: string, title: string | undefined) => void
  changeSize: (id: string, size: WidgetSizePreset) => void
  changeFilters: (id: string, filters: ReportWidgetFilters | undefined) => void
  changeCompare: (id: string, compare: ReportCompareOverride) => void
  changeVisualisation: (
    id: string,
    visualisation: ChartVisualisation | undefined
  ) => void
}

export const useReportBuildActions = (): ReportBuildActions => {
  const dispatch = useAppDispatch()
  const definition = useAppSelector((s) => s.reportCanvas.definition)
  const catalog = useAppSelector((s) => s.reportCanvas.catalog)
  const resolved = useAppSelector((s) => s.reportCanvas.resolved)
  const configWidgetId = useAppSelector((s) => s.reportCanvas.configWidgetId)

  const bySpecKey = useMemo(() => indexCatalogBySpecKey(catalog), [catalog])
  const specIndex = useMemo(() => indexCatalog(catalog), [catalog])

  const selectedWidget = useMemo(
    () =>
      configWidgetId
        ? (definition?.widgets.find((w) => w.id === configWidgetId) ?? null)
        : null,
    [definition, configWidgetId]
  )
  const selectedSpec = selectedWidget
    ? bySpecKey.get(selectedWidget.spec)
    : undefined

  const visualisationOptions = useMemo(() => {
    if (!selectedWidget) return null
    const entry = resolved[selectedWidget.id]
    const payload =
      entry?.resolved && selectedSpec
        ? adaptReportWidget(
            entry.resolved,
            selectedSpec,
            specIndex,
            selectedWidget.title,
            undefined
          ).payload
        : null
    return visualisationOptionsFor(selectedSpec?.type, payload)
  }, [selectedWidget, selectedSpec, resolved, specIndex])

  const apply = (next: ReturnType<typeof addWidget>) =>
    dispatch(reportCanvasApplyDefinition(next))

  return {
    selectedWidget,
    selectedSpec,
    visualisationOptions,
    addWidget: (spec) => {
      if (!definition) return
      apply(
        addWidget(definition, {
          id: crypto.randomUUID(),
          spec: spec.key,
          type: specWidgetType(spec.type)
        })
      )
    },
    selectWidget: (id) => dispatch(reportCanvasSetConfigWidgetId(id)),
    removeWidget: (id) => {
      if (!definition) return
      apply(removeWidget(definition, id))
      dispatch(reportCanvasSetConfigWidgetId(null))
    },
    changeTitle: (id, title) => {
      if (!definition) return
      apply(setWidgetTitle(definition, id, title))
    },
    changeSize: (id, size) => {
      if (!definition) return
      const spec = bySpecKey.get(
        definition.widgets.find((w) => w.id === id)?.spec ?? ""
      )
      apply(setWidgetSize(definition, id, size, specWidgetType(spec?.type)))
    },
    changeFilters: (id, filters) => {
      if (!definition) return
      apply(setWidgetFilters(definition, id, filters))
    },
    changeCompare: (id, compare) => {
      if (!definition) return
      apply(setWidgetCompare(definition, id, compare))
    },
    changeVisualisation: (id, visualisation) => {
      if (!definition) return
      apply(setWidgetVisualisation(definition, id, visualisation))
    }
  }
}
